import React, { useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { INTERAX_COLORS } from '../../../constants/colors'
import type { ActivityWithSessions } from '../../../types/GeneralTypes'
import { SprintTeamMembersCollapsible } from '../../../components/TrainingSprints/SprintTeamMembersCollapsible'
import { getTeamDisplayBucketsForActivity } from '../helpers/upcomingActivityTeamBuckets'

export interface GamerUpcomingActivityDetailsPanelProps {
  activity: ActivityWithSessions
}

export const GamerUpcomingActivityDetailsPanel: React.FC<GamerUpcomingActivityDetailsPanelProps> = ({ activity }) => {
  const { t } = useTranslation()

  const durationMinutes = useMemo(() => {
    if (!activity.startDate || !activity.endDate) return null
    return Math.round((new Date(activity.endDate).getTime() - new Date(activity.startDate).getTime()) / 60000)
  }, [activity.startDate, activity.endDate])

  const { teamDetails, unassignedMemberNames } = useMemo(
    () => getTeamDisplayBucketsForActivity(activity, t),
    [activity, t]
  )

  const teamsSectionLabels = useMemo(
    () => ({
      teamsAndParticipants: t('gamer.upcomingActivities.teamsSection.teamsAndParticipants'),
      teams: t('gamer.upcomingActivities.teamsSection.teams'),
      members: t('gamer.upcomingActivities.teamsSection.members'),
      noMembersAssigned: t('gamer.upcomingActivities.teamsSection.noMembersAssigned'),
      unassigned: t('gamer.upcomingActivities.teamsSection.unassigned')
    }),
    [t]
  )

  const showTeamsCollapsible = teamDetails.length > 0 || unassignedMemberNames.length > 0

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 2.5, md: 3 } }}>
      <Typography variant="body1" sx={{ color: INTERAX_COLORS.white }}>
        {activity.description || t('gamer.upcomingActivities.noDescription')}
      </Typography>

      {activity.sprintTitle ? (
        <Typography variant="body2" sx={{ color: INTERAX_COLORS.thunderGrey }}>
          <Box component="span" sx={{ fontWeight: 600, color: INTERAX_COLORS.white70 }}>
            {t('gamer.upcomingActivities.sprintLabel')}
          </Box>{' '}
          {activity.sprintTitle}
        </Typography>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr' },
          gap: { xs: 1, sm: 1.5, md: 2 }
        }}
      >
        {durationMinutes != null && durationMinutes >= 0 ? (
          <Typography variant="body2" sx={{ color: INTERAX_COLORS.thunderGrey }}>
            {t('gamer.upcomingActivities.details.duration', { minutes: durationMinutes })}
          </Typography>
        ) : null}
        <Typography variant="body2" sx={{ color: INTERAX_COLORS.thunderGrey }}>
          {t('gamer.upcomingActivities.details.teams', { count: activity.teamIds?.length ?? 0 })}
        </Typography>
        <Typography variant="body2" sx={{ color: INTERAX_COLORS.thunderGrey }}>
          {t('gamer.upcomingActivities.details.participants', { count: activity.participants?.length ?? 0 })}
        </Typography>
        <Typography variant="body2" sx={{ color: INTERAX_COLORS.thunderGrey }}>
          {t('gamer.upcomingActivities.details.sessions', { count: activity.sessions?.length ?? 0 })}
        </Typography>
      </Box>

      {showTeamsCollapsible ? (
        <SprintTeamMembersCollapsible
          teamDetails={teamDetails}
          unassignedMemberNames={unassignedMemberNames}
          labels={teamsSectionLabels}
        />
      ) : null}
    </Box>
  )
}
